// 他参加者が選択しているオブジェクトを、その参加者の色・アイコン・名前で表示するレイヤ。
// PO Board のエピックカード上プレゼンスと同じく、対象の端に小さな参加者バッジを重ねる。
// 同じ図形を選択している参加者のバッジは、図形ごとに 1 行へ横に並べ、選択枠の上側の輪郭へ密着させる
// （接点の求め方は remote-selection-badge-geometry.ts）。

import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import type { CanvasObject, PenStrokeCanvasObject, Viewer } from "@refinement-canvas/domain";
import {
  getRemoteAvatarAllowedHttpsOrigins,
  isRenderableAvatarImageUrl,
} from "../../auth/avatar-image-url";
import { getSharedStrokeOutlineVertices, getSharedStrokePath } from "../../lib/pen-stroke";
import { observeObjectLayout } from "./object-layout-observer";
import {
  PEN_SELECTION_OUTER_EXPANSION_SCREEN_PX,
  PenStrokeSelectionOutline,
} from "./objects/pen-stroke-selection-outline";
import {
  REMOTE_SELECTION_FRAME_RADIUS_PX,
  badgeRowOffset,
  computeFrameBadgeContact,
  computePenBadgeContact,
  safeInverseZoom,
  type StageBox,
} from "./remote-selection-badge-geometry";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import { useRenderedObjectHeight } from "./use-rendered-object-height";

interface Props {
  readonly currentUserId: string;
}

/** 1 つの図形と、それを選択している他参加者（表示順に並べたもの）。 */
export interface RemoteSelectionGroup {
  readonly object: CanvasObject;
  readonly viewers: readonly Viewer[];
}

const BADGE_HEIGHT_PX = 28;
// 横に並べたバッジどうしの間隔（画面 px）。
const BADGE_ROW_GAP_PX = 4;
// 選択枠・バッジの行の重なり順。図形より手前、自分の選択表示（MAX_SAFE_INTEGER）の一段下に出す（従来の値を保つ）。
const REMOTE_SELECTION_Z_INDEX = Number.MAX_SAFE_INTEGER - 1;
// 名前の行の読み上げ名。行の中の各名前は 1 回ずつ読まれる（アイコンは読み上げから外す）。
const BADGE_ROW_ARIA_LABEL = "この図形を選択しているユーザー";

export function CanvasRemoteSelectionsLayer({ currentUserId }: Props): JSX.Element {
  const objects = useCanvasDocStore((s) => s.objects);
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const groups = buildRemoteSelectionGroups(Object.values(viewers), objects, currentUserId);

  return (
    <div
      data-testid="canvas-remote-selections-layer"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      {groups.map(({ object, viewers: groupViewers }) => (
        <RemoteSelectionGroupView key={object.id} object={object} viewers={groupViewers} />
      ))}
    </div>
  );
}

/**
 * 他参加者の選択を図形ごとにまとめる。
 *
 * - viewers store と同じく userId ごとに最後の値を採る（配列から直接呼ぶ場合も 1 人 1 件にする）。
 * - 自分自身と、存在しない図形への選択は除く。同じ参加者が同じ図形を重ねて選択していても 1 件にする。
 * - 各図形の参加者は、表示名の日本語順、同名なら userId の順に並べる（届いた順では入れ替えない）。
 */
export function buildRemoteSelectionGroups(
  viewers: readonly Viewer[],
  objects: Record<string, CanvasObject>,
  currentUserId: string
): readonly RemoteSelectionGroup[] {
  const latestByUserId = new Map<string, Viewer>();
  for (const viewer of viewers) latestByUserId.set(viewer.userId, viewer);

  const groups = new Map<string, { object: CanvasObject; viewers: Viewer[] }>();
  for (const viewer of [...latestByUserId.values()].sort(compareViewer)) {
    if (viewer.userId === currentUserId) continue;
    for (const objectId of new Set(viewer.selectedObjectIds ?? [])) {
      // 他参加者が送ってくる id は信頼できないため、doc store 自身が持つ図形だけを引く
      // （"constructor" などの id で Object.prototype の値を拾わない）。
      if (!Object.hasOwn(objects, objectId)) continue;
      const object = objects[objectId];
      if (!object) continue;
      const group = groups.get(objectId);
      if (group) {
        group.viewers.push(viewer);
      } else {
        groups.set(objectId, { object, viewers: [viewer] });
      }
    }
  }

  return [...groups.values()];
}

function RemoteSelectionGroupView({
  object,
  viewers,
}: {
  readonly object: CanvasObject;
  readonly viewers: readonly Viewer[];
}): JSX.Element {
  // テキスト図形は本文量により実描画高さが object.h（minHeight 運用）を超え得る。object.h のままだと
  // 枠が本文より短くなり、末尾行の相手キャレットが枠の下へはみ出すため、自分の選択枠と同じく実測する。
  // 枠とバッジの行は同じ表示高さを使う（別々に求めると、再レイアウトの瞬間に枠と行がずれる）。
  const renderedHeight = useRenderedObjectHeight(object.id, object.type === "text");
  const boxHeight =
    object.type === "text" && renderedHeight !== null
      ? Math.max(object.h, renderedHeight)
      : object.h;

  return (
    <>
      {viewers.map((viewer) => (
        <RemoteSelectionFrame
          key={`${viewer.userId}:${object.id}`}
          boxHeight={boxHeight}
          object={object}
          viewer={viewer}
        />
      ))}
      <RemoteSelectionBadgeRow boxHeight={boxHeight} object={object} viewers={viewers} />
    </>
  );
}

function RemoteSelectionFrame({
  boxHeight,
  object,
  viewer,
}: {
  readonly boxHeight: number;
  readonly object: CanvasObject;
  readonly viewer: Viewer;
}): JSX.Element {
  const color = normalizeViewerColor(viewer.color);
  const containerStyle: CSSProperties = {
    position: "absolute",
    left: `${object.x}px`,
    top: `${object.y}px`,
    width: `${object.w}px`,
    height: `${boxHeight}px`,
    pointerEvents: "none",
    zIndex: REMOTE_SELECTION_Z_INDEX,
  };
  const frameStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    // 枠線は拡大率に依らず画面上 2px に見せる（自分の選択アウトラインと同じ補正）。
    border: `calc(2px * var(--canvas-inv-zoom, 1)) solid ${color}`,
    // borderRadius と boxShadow は補正せずステージ px のまま残す。枠は図形の外接矩形そのもので
    // scale ラッパへ包めず、角丸は倍率が変わっても「角丸である」認識に影響せず、影は装飾のため。
    // 角丸の半径はバッジの接点の算出と同じ定数を使う（輪郭の形を一致させる）。
    borderRadius: `${REMOTE_SELECTION_FRAME_RADIUS_PX}px`,
    boxSizing: "border-box",
    transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
    transformOrigin: "center",
    boxShadow: `0 0 0 1px rgba(255, 255, 255, 0.9), 0 6px 16px ${withAlpha(color, 0.18)}`,
    pointerEvents: "none",
  };

  return (
    <div
      data-testid={`remote-selection-${viewer.userId}-${object.id}`}
      style={containerStyle}
    >
      {object.type === "pen-stroke" ? (
        // ペンストロークだけは外接矩形の枠を出さず、自分の選択表示と同じストローク形状の
        // 輪郭をその参加者の色で描く（共同編集中だけ四角い枠が現れるのを防ぐ）。
        <RemotePenStrokeSelectionOutline color={color} object={object} viewer={viewer} />
      ) : (
        <div
          aria-hidden="true"
          data-testid={`remote-selection-frame-${viewer.userId}-${object.id}`}
          style={frameStyle}
        />
      )}
    </div>
  );
}

/**
 * 1 つの図形を選択している他参加者のバッジを、左から右へ 1 行に並べる。
 *
 * 行全体を逆スケール（scale(--canvas-inv-zoom)、原点は左下）して画面上の寸法を倍率に依らず一定にし、
 * 先頭バッジの下面中央を接点へ合わせる。回転していない十分な幅の枠では、行の左端が枠の左端、
 * 行の下端が枠の上辺にそろう（left: 0 / bottom: 100% で、translateY は使わない）。
 * 接点の算出に先頭バッジの実測幅を使うため、最初の実測までは名前の行だけを visibility: hidden にする
 * （誤った位置を一瞬でも描かない。枠は表示したまま）。実測は毎回の commit の layout effect で行い
 * （名前の変更などでも、その commit のうちに位置を直す）、フォントの読み込みなど commit を伴わない
 * 大きさの変化は、図形本体と同じ観測（object-layout-observer）で拾う。
 */
function RemoteSelectionBadgeRow({
  boxHeight,
  object,
  viewers,
}: {
  readonly boxHeight: number;
  readonly object: CanvasObject;
  readonly viewers: readonly Viewer[];
}): JSX.Element {
  const zoom = useCanvasStore((s) => s.view.zoom);
  const rowRef = useRef<HTMLDivElement>(null);
  const firstBadgeScreenWidth = useFirstBadgeScreenWidth(rowRef);
  const penVertices = useMemo(
    () => (object.type === "pen-stroke" ? getSharedStrokeOutlineVertices(object) : null),
    [object]
  );

  const box: StageBox = { x: object.x, y: object.y, w: object.w, h: boxHeight };
  // 画面上の幅をステージ座標へ換算する（行は逆スケールされ、画面上の幅が倍率に依らない）。
  const firstBadgeWidth = (firstBadgeScreenWidth ?? 0) * safeInverseZoom(zoom);
  const penContact = penVertices
    ? computePenBadgeContact({
        vertices: penVertices,
        box,
        rotationDeg: object.rotation,
        ringOuterScreenPx: PEN_SELECTION_OUTER_EXPANSION_SCREEN_PX,
        zoom,
      })
    : null;
  // ペンのアウトラインが空のときは、枠と同じ求め方（外接矩形の上辺）へ戻す。
  const contact =
    penContact ??
    computeFrameBadgeContact({ box, rotationDeg: object.rotation, firstBadgeWidth, zoom });
  const offset = badgeRowOffset(contact, box, firstBadgeWidth);

  return (
    <div
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${boxHeight}px`,
        pointerEvents: "none",
        zIndex: REMOTE_SELECTION_Z_INDEX,
      }}
    >
      <div
        ref={rowRef}
        aria-label={BADGE_ROW_ARIA_LABEL}
        data-testid={`remote-selection-badge-row-${object.id}`}
        role="group"
        style={{
          position: "absolute",
          left: `${offset.left}px`,
          // 行の下端を接点の高さ（図形の外接矩形の上端から offset.top）へ置く。
          bottom: rowBottom(offset.top),
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "flex-end",
          gap: `${BADGE_ROW_GAP_PX}px`,
          // 図形の幅に関わらず 1 行のまま並べる（狭い図形でも折り返さず、バッジも縮めない）。
          width: "max-content",
          // 行全体へ 1 回だけ逆スケールを掛ける。原点を左下にし、行の下端の位置を倍率で動かさない。
          transform: "scale(var(--canvas-inv-zoom, 1))",
          transformOrigin: "left bottom",
          visibility: firstBadgeScreenWidth === null ? "hidden" : undefined,
          pointerEvents: "none",
        }}
      >
        {viewers.map((viewer) => (
          <RemoteSelectionBadge key={viewer.userId} objectId={object.id} viewer={viewer} />
        ))}
      </div>
    </div>
  );
}

/**
 * 行の先頭バッジの画面上の幅（px）。正の幅を測れるまでは null（行を隠す）。
 * 値が変わったときだけ state を更新するため、毎 commit の実測でも再レンダーを誘発し続けない。
 */
function useFirstBadgeScreenWidth(rowRef: RefObject<HTMLDivElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  // 観測通知を受けたときに、毎 commit の layout effect をもう一度走らせるための契機。
  const [, bumpLayoutSignal] = useState(0);
  const observedRowRef = useRef<HTMLElement | null>(null);
  const unobserveRef = useRef<(() => void) | null>(null);

  // 毎 commit の layout effect で、購読（行の要素が変わったときだけ張り替える）と実測をこの順に行う。
  // 購読を済ませてから測るため、測った後・購読する前に Web フォントの読み込みが終わって幅が変わっても
  // 取りこぼさない（図形本体の実測高さ〈use-rendered-object-height〉と同じ組み立て）。
  // ResizeObserver はモジュール内の 1 つを図形本体の観測と共有する（要素ごとに作らない）。
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (observedRowRef.current !== row) {
      unobserveRef.current?.();
      unobserveRef.current = null;
      observedRowRef.current = row;
      if (row) {
        unobserveRef.current = observeObjectLayout(row, () =>
          bumpLayoutSignal((value) => value + 1)
        );
      }
    }
    const first = row?.firstElementChild;
    const measured = first instanceof HTMLElement ? first.getBoundingClientRect().width : 0;
    // 幅 0 は、まだ配置されていない（非表示の祖先の中など）とみなし、実測済みにしない。
    // 配置されて大きさが変われば、上の観測が通知して測り直す。
    const next = Number.isFinite(measured) && measured > 0 ? measured : null;
    setWidth((previous) => (previous === next ? previous : next));
  });

  // アンマウント時に購読を解放する（layout effect は毎 commit 走るため、そこでは解除しない）。
  useEffect(
    () => () => {
      unobserveRef.current?.();
      unobserveRef.current = null;
      observedRowRef.current = null;
    },
    []
  );

  return width;
}

/** 行の下端の位置。接点が外接矩形の上端より下なら上へ、上なら下へずらす。 */
function rowBottom(top: number): string {
  if (top === 0) return "100%";
  return top > 0 ? `calc(100% - ${top}px)` : `calc(100% + ${-top}px)`;
}

function RemoteSelectionBadge({
  objectId,
  viewer,
}: {
  readonly objectId: string;
  readonly viewer: Viewer;
}): JSX.Element {
  const color = normalizeViewerColor(viewer.color);
  return (
    <div
      data-testid={`remote-selection-badge-${viewer.userId}-${objectId}`}
      style={{
        display: "inline-flex",
        flex: "0 0 auto",
        alignItems: "center",
        gap: "6px",
        maxWidth: "240px",
        minHeight: `${BADGE_HEIGHT_PX}px`,
        padding: "3px 9px 3px 3px",
        border: `1px solid ${color}`,
        borderRadius: "9999px",
        background: "var(--color-surface)",
        boxShadow: "var(--shadow-1)",
        color: "var(--color-text)",
        fontSize: "12px",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <ViewerAvatar viewer={viewer} />
      {/* 表示は 240px で省略するが、テキストは全文を持つ（読み上げ名は完全名になる）。 */}
      <span
        style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {viewer.displayName}
      </span>
    </div>
  );
}

/**
 * 他参加者が選択中のペンストロークの輪郭。外接矩形と同じ位置・大きさの <svg> を容器へ重ね、
 * 自分の選択表示と同じ部品・同じアウトラインで、色だけをその参加者の色にして描く。
 */
function RemotePenStrokeSelectionOutline({
  color,
  object,
  viewer,
}: {
  readonly color: string;
  readonly object: PenStrokeCanvasObject;
  readonly viewer: Viewer;
}): JSX.Element {
  // 自分の選択表示（pen-stroke-object.tsx）と同じ関数でアウトラインを求め、幾何を一致させる。
  // 共有メモを通すため、同じストロークを複数の参加者が選択しても算出は 1 回で済む。
  const path = useMemo(() => getSharedStrokePath(object), [object]);
  // mask の id は mask="url(#...)" という CSS 構文の一部になるため、利用者由来の文字列を入れない。
  // userId（IdP の sub）と object.id はサーバスキーマが長さだけを制限しており、空白・")"・"#" などが
  // 入り得る。React の useId() は同一文書内で一意な値を返すため、そのまま連結して使う。
  // 戻り値から文字を削って整形してはいけない（React のバージョンごとに形式が異なり、
  // 削った結果が別の値と縮退しない保証が無い）。
  const maskId = `remote-pen-selection-mask-${useId()}`;
  return (
    <svg
      aria-hidden="true"
      data-testid={`remote-selection-outline-${viewer.userId}-${object.id}`}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        // 絶対配置の置換要素（svg）は inset だけでは伸びないため、容器いっぱいの幅と高さを明示する。
        width: "100%",
        height: "100%",
        // 輪郭はマージン分だけ外接矩形の外へはみ出す。
        overflow: "visible",
        pointerEvents: "none",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
      }}
      // points は stage 座標のまま保存されているため、自分用の svg と同じ viewBox で座標を一致させる。
      viewBox={`${object.x} ${object.y} ${object.w} ${object.h}`}
    >
      <PenStrokeSelectionOutline
        color={color}
        maskId={maskId}
        object={object}
        path={path}
        testId={`remote-selection-outline-path-${viewer.userId}-${object.id}`}
      />
    </svg>
  );
}

const AVATAR_COMMON_STYLE: CSSProperties = {
  width: "22px",
  height: "22px",
  borderRadius: "9999px",
  flex: "0 0 auto",
};

function ViewerAvatar({ viewer }: { readonly viewer: Viewer }): JSX.Element {
  // 他参加者が送ってくる avatarImageUrl は信頼できないため、data:image/... か信頼済み origin（IdP）
  // の https のみ描画する。任意 https による強制外部リクエスト/トラッキングを防ぐ。
  const avatarImageUrl = viewer.avatarImageUrl;
  if (
    avatarImageUrl &&
    isRenderableAvatarImageUrl(avatarImageUrl, {
      allowedHttpsOrigins: getRemoteAvatarAllowedHttpsOrigins(),
    })
  ) {
    // URL ごとに作り直す（key）。読み込みの失敗は URL ごとに持ち、別の URL から戻したときも読み込み直す。
    return <ViewerAvatarImage key={avatarImageUrl} url={avatarImageUrl} viewer={viewer} />;
  }

  return <ViewerAvatarInitials viewer={viewer} />;
}

/** アイコン画像。読み込みに失敗したら、同じ URL のあいだはイニシャルを表示する。 */
function ViewerAvatarImage({
  url,
  viewer,
}: {
  readonly url: string;
  readonly viewer: Viewer;
}): JSX.Element {
  const [failed, setFailed] = useState(false);
  if (failed) return <ViewerAvatarInitials viewer={viewer} />;
  return (
    // 名前は隣のテキストで読まれるため、アイコンは代替テキストを持たない（名前を二重に読ませない）。
    <img
      alt=""
      onError={() => setFailed(true)}
      referrerPolicy="no-referrer"
      src={url}
      style={{
        ...AVATAR_COMMON_STYLE,
        objectFit: "cover",
      }}
    />
  );
}

/** 名前の先頭 2 文字のイニシャル。名前は隣のテキストで読まれるため、読み上げから外す。 */
function ViewerAvatarInitials({ viewer }: { readonly viewer: Viewer }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        ...AVATAR_COMMON_STYLE,
        display: "inline-grid",
        placeItems: "center",
        background: normalizeViewerColor(viewer.color),
        color: "#ffffff",
        fontSize: "10px",
        lineHeight: 1,
      }}
    >
      {getFallbackLabel(viewer.displayName)}
    </span>
  );
}

function compareViewer(left: Viewer, right: Viewer): number {
  const byName = left.displayName.localeCompare(right.displayName, "ja");
  if (byName !== 0) return byName;
  return left.userId.localeCompare(right.userId, "ja");
}

function getFallbackLabel(displayName: string): string {
  const trimmed = displayName.trim().replace(/\s+/gu, "");
  if (trimmed.length === 0) return "?";
  return Array.from(trimmed).slice(0, 2).join("");
}

function normalizeViewerColor(color: string): string {
  return color.trim().length > 0 ? color : "#1e63d4";
}

function withAlpha(color: string, alpha: number): string {
  if (!/^#[0-9a-f]{6}$/iu.test(color)) return `rgba(30, 99, 212, ${alpha})`;
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
